/* Widget-level actions: the download formats, and full screen.
 *
 * Download used to live only in the editor toolbar (Editor/index.js handleDownloadCSV),
 * which meant a published board had no way to export at all - the board toolbar had an
 * Export button but its handler had no branch for it. Every widget header needs the same
 * routine, so it lives here and the editor calls it too rather than keeping a second copy.
 *
 * ── FOUR FORMATS, ALL IN THE BROWSER ──
 * PDF, PNG, XLS and CSV are built here from data the page already holds - no server
 * endpoint is involved. (The API does carry /generate-pdf-report and
 * /generate-xlsx-report, but those re-fetch the source data server-side and can only
 * produce the whole board; what a reader wants is the widget in front of them, filtered
 * the way they filtered it.)
 *
 * PDF, PNG and XLS are always offered. CSV is the one format behind a switch - the
 * "csvdownload" widget/board property the editor writes - which is why only its caller
 * checks a flag.
 *
 * Every row, not every row ON SCREEN: the table paginates, so nothing here reads the
 * rendered DOM. Each format is written from the same array the table is drawing from,
 * after grouping and after data filters, so a file matches what the reader is looking at
 * and carries the rows below the fold with it.
 *
 * xlsx, jspdf and dom-to-image are heavy (~1MB together) and most sessions never export
 * anything, so all three are import()ed at the moment they are used. That is why the
 * format functions are async.
 */

/**
 * Serialize a cell value for CSV.
 * - Primitives → as-is
 * - Arrays of objects → "{ Key: Val; Key: Val } | { Key: Val; Key: Val }"
 * - Plain objects → "Key: Val; Key: Val"
 * - Arrays of primitives → pipe-joined
 */
export const serializeCellValue = (val) => {
  if (val === null || val === undefined) return "";

  // primitive
  if (typeof val !== "object") return String(val);

  // array
  if (Array.isArray(val)) {
    if (val.length === 0) return "";

    return val
      .map((item) => {
        if (item === null || item === undefined) return "";
        if (typeof item !== "object") return String(item);

        const itemEntries = Object.entries(item);
        return `{ ${itemEntries.map(([k, v]) => `${k}: ${serializeCellValue(v)}`).join("; ")} }`;
      })
      .join(" | ");
  }

  // plain object
  const entries = Object.entries(val);
  if (entries.length === 0) return "";

  return entries.map(([k, v]) => `${k}: ${serializeCellValue(v)}`).join("; ");
};

/* Every field is quoted and embedded quotes are doubled, so a value containing a comma
   or a line break does not shift the following columns. */
export const buildCsvString = (headers, rows) =>
  [
    headers.join(","),
    ...rows.map((row) =>
      headers
        .map((h) => `"${String(row[h] ?? "").replace(/"/g, '""')}"`)
        .join(","),
    ),
  ].join("\n");

/* The same quoting, straight off a header row and rows of cells - no intermediate object
   keyed by column name, so two columns sharing a display name both survive. */
export const buildCsvMatrixString = ({ headers, body }) =>
  [headers, ...body].map((row) => row.map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");

export const triggerCsvDownload = (csvString, fileName) => {
  const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const stamp = () => new Date().toISOString().slice(0, 10);
// PNG names carry the time too - a chart exported twice in a day is two different pictures
const timeStamp = () => new Date().toISOString().slice(0, 19).replace("T", "_").replace(/:/g, "-");

/* A canvas may not be more than about 16k pixels a side, and a row is 26 of them, so one
   picture holds roughly 600 rows. A longer table is split across several images rather
   than refused - see exportRowsToPng. This caps how many: twenty images is around 12,000
   rows, past which someone is asking for a wall of pictures and means Excel or CSV. */
export const PNG_MAX_IMAGES = 20;

/* ── TWO DESCRIPTIONS OF A COLUMN, AND WE ARE HANDED EITHER ──
   The editor exports from a resource's `availableFields` - {fieldName, fieldDisplayName,
   checked}. A published widget exports from the list it built for the table itself, which
   is an Ant Design column - {title, dataIndex, key, fieldDisplayName} with no fieldName
   on it at all (BoardDataTableContainer builds it around line 106).

   Reading only `fieldName` was why an export off a board came out as the right number of
   columns with nothing in any of them: every header resolved to undefined, and every cell
   was looked up as row[undefined]. That is worth knowing about the CSV button too - it
   read the same way, so it had always written empty files from a board; the formats added
   alongside it only made it visible.

   Both shapes are answered here, once, so no format has to know which one it was given. */
const normalizeField = (field) => {
  if (!field) return null;
  if (typeof field === "string") return { key: field, header: field, checked: true, width: null };

  const key = field.fieldName || field.dataIndex || field.key;
  if (!key) return null;

  /* The width the author dragged this column to, in px - saved on the field by
     carryFieldWidths (redux/editorSlice.js) and read back here so an exported file has
     the same proportions as the table it is a copy of. Every format used to size its own
     columns from the content, so a report whose columns had been deliberately widened
     came out looking nothing like it. */
  const width = Number(field.width);

  return {
    // fieldDisplayName is what the author renamed the column to; title is Ant's own label
    header: field.fieldDisplayName || field.title || key,
    key,
    // an Ant column carries no `checked` - it is in the list because it is on screen
    checked: field.checked !== false,
    width: Number.isFinite(width) && width > 0 ? width : null,
  };
};

const visibleFieldsOf = (fields) => (fields || []).map(normalizeField).filter((f) => f && f.checked);

/* The one shape every format is written from: the header row, and the rows as strings,
   hidden columns already dropped. Built once here so a PDF, a sheet and a picture of the
   same widget can never disagree about what was in it. */
export const buildExportMatrix = ({ rows, fields }) => {
  const visible = visibleFieldsOf(fields);
  const dataRows = Array.isArray(rows) ? rows : [];
  if (!visible.length || !dataRows.length) return null;

  return {
    headers: visible.map((f) => f.header),
    body: dataRows.map((row) => visible.map((f) => serializeCellValue(row[f.key] ?? ""))),
    // the author's column widths in px, null where a column has none
    widths: visible.map((f) => f.width),
  };
};

/* A chart plots the group-by field against the summed value field. Every format exports
   that series rather than the rows underneath it - the series is what is on screen. */
export const buildChartSeries = ({ rows, groupByField, valueField }) => {
  const dataRows = Array.isArray(rows) ? rows : rows?.Result || rows?.data || [];
  if (!groupByField || !valueField || !dataRows.length) return null;

  const grouped = dataRows.reduce((acc, row) => {
    const key = row[groupByField];
    acc[key] = (acc[key] || 0) + Number(row[valueField] || 0);
    return acc;
  }, {});

  const entries = Object.entries(grouped);
  return entries.length ? entries.map(([name, value]) => ({ name, value })) : null;
};

/* What goes above the data in every format: what this is, where it came from, and which
   filters were applied when it was taken. Without the filters a reader cannot tell an
   export of everything from an export of one branch's figures.

   ── WHICH NAME HEADS THE FILE ──
   The board's name, with the widget's underneath it - the page header first, then which
   widget on the page this came from, in that order.

   On a report, and on a dashboard holding a single widget, those two are the same name:
   ReportWidjets.widgetTitleFor gives a lone widget the board's name, because the page
   header IS its title and it draws no header of its own. The second line is dropped
   rather than printing the name twice. */
export const buildHeadingLines = ({ boardName, widgetName, filters }) => {
  const title = boardName || widgetName || "Report";
  const lines = [];

  if (widgetName && widgetName !== title) lines.push(["", widgetName]);
  (filters || []).forEach((f, i) => lines.push([i === 0 ? "Filters" : "", f]));

  return { title, lines };
};

const buildFooterLines = ({ generatedBy }) => {
  const now = new Date();
  const lines = [["Generated", `${now.toLocaleDateString()} ${now.toLocaleTimeString()}`]];
  if (generatedBy) lines.push(["Generated by", generatedBy]);
  return lines;
};

/* Human-readable descriptions of the data filters in force, for the heading block.
   dataFilterEngine's own shapes - see utils/dataFilterEngine.js. */
export const describeDataFilters = (filters) =>
  (filters || [])
    .filter((f) => f && f.fieldName)
    .map((f) => {
      const field = f.fieldDisplayName || f.fieldName;
      const type = f.filterType || "equals";
      if (type === "between") return `${field}: ${f.value1 ?? ""} – ${f.value2 ?? ""}`;
      const value = Array.isArray(f.value) ? f.value.join(", ") : (f.value ?? "");
      return `${field}: ${type} ${value}`.trim();
    });

/* Everything below exports what the user configured, not the raw API payload: hidden
   columns are left out and headers use the display name. An export that did not match
   the table on screen would look like the wrong data. */

/**
 * Table widget → one row per record, one column per checked field.
 * Returns false when there was nothing to write, so the caller can say why.
 */
export const exportRowsToCsv = ({ rows, fields, widgetName }) => {
  /* Written from the same matrix as the other three formats, so a CSV and a PDF of one
     widget cannot disagree. It used to read the field list its own way - which is how it
     came to write files of empty columns off a board - and keyed its rows by header name,
     which silently dropped a column whenever two shared a display name. */
  const matrix = buildExportMatrix({ rows, fields });
  if (!matrix) return false;

  triggerCsvDownload(buildCsvMatrixString(matrix), `${widgetName || "report"}_${stamp()}.csv`);
  return true;
};

/**
 * Chart widget → the plotted series, not the underlying rows: the group-by field and the
 * summed value field, grouped exactly the way ChartContainer groups them for display.
 */
export const exportChartToCsv = ({ rows, groupByField, valueField, widgetName, boardName }) => {
  const series = buildChartSeries({ rows, groupByField, valueField });
  if (!series) return false;

  const headers = [groupByField, valueField];
  const csvRows = series.map(({ name, value }) => ({ [groupByField]: name, [valueField]: value }));

  // the board's name when the chart has none of its own - see BoardChartContainer
  triggerCsvDownload(buildCsvString(headers, csvRows), `${widgetName || boardName || "chart"}_${stamp()}.csv`);
  return true;
};

/* ── LOADING THE LIBRARIES ──
   Each of these ships in a different shape - xlsx and downloadjs are CommonJS, jspdf
   exports jsPDF by name, jspdf-autotable a default function - and the interop wrapper
   webpack puts around a CommonJS module is not always the module itself. Each loader
   reaches for what it needs and falls back, so a bundler change cannot silently turn one
   of them into `undefined is not a function` at the moment someone clicks Download. */
const loadXlsx = async () => {
  const mod = await import("xlsx");
  return mod.utils ? mod : mod.default;
};

const loadJsPdf = async () => {
  const mod = await import("jspdf");
  return mod.jsPDF || mod.default;
};

const loadAutoTable = async () => {
  const mod = await import("jspdf-autotable");
  return mod.default || mod;
};

const loadDomToImage = async () => {
  const mod = await import("dom-to-image");
  return mod.toPng ? mod : mod.default;
};

const loadDownload = async () => {
  const mod = await import("downloadjs");
  return mod.default || mod;
};

/* ─────────────────────────────────────────────────────────────
   XLS
   One sheet: a heading block (board, widget, filters), the table under it, and a
   generated-on footer - so a sheet mailed on to someone says what it is a sheet of.
   XLSX.writeFile triggers the browser's own save, so there is no blob to clean up.
   ───────────────────────────────────────────────────────────── */
const writeSheet = async ({ headers, body, widths, widgetName, boardName, filters, generatedBy }) => {
  const XLSX = await loadXlsx();

  const { title, lines } = buildHeadingLines({ boardName, widgetName, filters });
  // blank rows keep the heading and the footer off the table itself, so the data block
  // is still a clean rectangle to sort or pivot
  const aoa = [
    [title],
    ...lines.map(([label, value]) => (label ? [label, value] : [value])),
    [],
    headers,
    ...body,
    [],
    ...buildFooterLines({ generatedBy }),
  ];

  const sheet = XLSX.utils.aoa_to_sheet(aoa);
  /* Column widths: the author's, where they set one. Excel measures in characters rather
     than pixels, and ~7px is one character at the default font.

     Otherwise the header, or the widest cell, whichever is longer - capped, or one long
     description stretches a column past the width of the screen. Folded rather than
     spread into Math.max: a 60,000-row export would spread 60,000 arguments into one
     call and overflow the stack. */
  sheet["!cols"] = headers.map((h, i) => {
    const px = widths?.[i];
    if (Number.isFinite(px) && px > 0) return { wch: Math.max(6, Math.min(120, Math.round(px / 7))) };

    return {
      wch: Math.min(
        60,
        body.reduce(
          (widest, row) => Math.max(widest, String(row[i] ?? "").length + 2),
          Math.max(10, String(h).length + 2),
        ),
      ),
    };
  });

  const book = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(book, sheet, "Report");
  XLSX.writeFile(book, `${widgetName || boardName || "report"}_${stamp()}.xlsx`);
  return true;
};

export const exportRowsToXlsx = async ({ rows, fields, widgetName, boardName, filters, generatedBy }) => {
  const matrix = buildExportMatrix({ rows, fields });
  if (!matrix) return false;
  return writeSheet({ ...matrix, widgetName, boardName, filters, generatedBy });
};

export const exportChartToXlsx = async ({ rows, groupByField, valueField, widgetName, boardName, filters, generatedBy }) => {
  const series = buildChartSeries({ rows, groupByField, valueField });
  if (!series) return false;
  return writeSheet({
    headers: [groupByField, valueField],
    body: series.map(({ name, value }) => [name, value]),
    widgetName,
    boardName,
    filters,
    generatedBy,
  });
};

/* ─────────────────────────────────────────────────────────────
   PDF
   A table is drawn by autoTable from the same rows, NOT photographed: the text stays
   selectable and searchable, and the rows below the fold come with it. A chart has no
   text to draw, so it is photographed.
   ───────────────────────────────────────────────────────────── */
const pdfText = (value) => (value === null || value === undefined ? "" : String(value));

const drawPdfHeading = (doc, { boardName, widgetName, filters }) => {
  const { title, lines } = buildHeadingLines({ boardName, widgetName, filters });
  let y = 40;

  doc.setFontSize(14);
  doc.setTextColor(15, 37, 87);
  doc.text(pdfText(title), 40, y);

  doc.setFontSize(9);
  doc.setTextColor(100, 116, 139);
  lines.forEach((line) => {
    y += 14;
    doc.text(`${line[0] ? `${line[0]}: ` : ""}${pdfText(line[1])}`, 40, y);
  });

  return y + 16;
};

const drawPdfFooter = (doc, y, { generatedBy }) => {
  doc.setFontSize(8);
  doc.setTextColor(148, 163, 184);
  buildFooterLines({ generatedBy }).forEach((line, i) => {
    doc.text(`${line[0]}: ${pdfText(line[1])}`, 40, y + 16 + i * 12);
  });
};

/* ── HOW WIDE THE PAGE IS ──
   A table of forty columns fits on no standard paper size, and there are only two ways
   to deal with that:

     1. break the columns across pages - the reader gets columns 1-9 on page one and the
        rest stranded in a block further down, and has to hold one row together in their
        head across pages
     2. make the page as wide as the table needs, and let the reader zoom

   This does (2). Keeping a row on one line is the whole point of a table, and zooming is
   something every PDF reader does well. (1) is what autoTable's horizontalPageBreak
   gives, and it is what this used to do.

   PDF allows a page up to 14400pt a side, so that is the ceiling; past it every column
   is scaled by the same factor, which keeps the author's proportions. */
const PDF_MAX_PAGE_PT = 14000;
const PT_PER_PX = 0.75;
const DEFAULT_COL_PX = 130;
const PDF_MARGIN = 40;

const pdfColumnWidths = (headers, widths) => {
  const raw = headers.map((_h, i) => {
    const px = widths?.[i];
    return (Number.isFinite(px) && px > 0 ? px : DEFAULT_COL_PX) * PT_PER_PX;
  });

  const total = raw.reduce((sum, w) => sum + w, 0);
  const room = PDF_MAX_PAGE_PT - PDF_MARGIN * 2;
  if (total <= room) return raw;

  const scale = room / total;
  return raw.map((w) => w * scale);
};

export const exportRowsToPdf = async ({ rows, fields, widgetName, boardName, filters, generatedBy }) => {
  const matrix = buildExportMatrix({ rows, fields });
  if (!matrix) return false;

  const [jsPDF, autoTable] = await Promise.all([loadJsPdf(), loadAutoTable()]);

  const columns = pdfColumnWidths(matrix.headers, matrix.widths);
  const tableWidth = columns.reduce((sum, w) => sum + w, 0);

  /* The page is sized to the table rather than the table squeezed into the page. Height
     stays A4 landscape - rows still paginate downwards, which reads normally. */
  const pageWidth = Math.max(842, Math.ceil(tableWidth + PDF_MARGIN * 2));
  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: [pageWidth, 595] });

  const startY = drawPdfHeading(doc, { boardName, widgetName, filters });

  /* Each column pinned to the author's width. tableWidth "wrap" stops autoTable
     stretching them to fill the page, which would undo it. */
  const columnStyles = {};
  columns.forEach((w, i) => {
    columnStyles[i] = { cellWidth: w };
  });

  autoTable(doc, {
    head: [matrix.headers],
    body: matrix.body,
    startY,
    margin: { left: PDF_MARGIN, right: PDF_MARGIN },
    tableWidth: "wrap",
    columnStyles,
    styles: {
      overflow: "linebreak",
      fontSize: 8,
      cellPadding: 4,
    },
    headStyles: { fillColor: [15, 37, 87], textColor: 255, fontStyle: "bold" },
    alternateRowStyles: { fillColor: [248, 250, 252] },
  });

  drawPdfFooter(doc, doc.lastAutoTable?.finalY || startY, { generatedBy });
  doc.save(`${widgetName || boardName || "report"}_${stamp()}.pdf`);
  return true;
};

/* A chart: photograph it, then place the picture on a page sized to its own proportions
   so it is not letterboxed inside a shape it was never drawn for. */
export const exportNodeToPdf = async ({ node, widgetName, boardName, filters, generatedBy }) => {
  if (!node) return false;

  const width = node.scrollWidth || node.offsetWidth;
  const height = node.scrollHeight || node.offsetHeight;
  if (!width || !height) return false;

  // same guarded capture as the PNG - a stub data URL would land in the page as a blank box
  const [jsPDF, dataUrl] = await Promise.all([loadJsPdf(), captureNode(node)]);

  const doc = new jsPDF({ orientation: width >= height ? "landscape" : "portrait", unit: "pt", format: "a4" });
  const startY = drawPdfHeading(doc, { boardName, widgetName, filters });
  const pageWidth = doc.internal.pageSize.getWidth() - 80;
  const pageHeight = doc.internal.pageSize.getHeight() - startY - 60;
  // fit inside the page in both directions, keeping the chart's own proportions
  const scale = Math.min(pageWidth / width, pageHeight / height);

  doc.addImage(dataUrl, "PNG", 40, startY, width * scale, height * scale);
  drawPdfFooter(doc, startY + height * scale, { generatedBy });
  doc.save(`${widgetName || boardName || "chart"}_${stamp()}.pdf`);
  return true;
};

/* ─────────────────────────────────────────────────────────────
   PNG
   ───────────────────────────────────────────────────────────── */

/* ── WHY THE TABLE'S PICTURE IS DRAWN, NOT PHOTOGRAPHED ──
   The first version of this built a standalone HTML table off-screen and handed it to
   dom-to-image. It produced files Windows Photos would not open, after a long wait, and
   for two reasons that both come with photographing a DOM node:

   1. dom-to-image clones the node into an SVG foreignObject, and to make the clone look
      right it walks EVERY stylesheet in the document and inlines every web font it finds.
      This app links Material Icons from fonts.googleapis.com, so on a machine where that
      request is slow or blocked the export sat waiting on the network - the "long time" -
      for fonts our table never used.
   2. A table of a few hundred rows is taller than a canvas is allowed to be (browsers cap
      a canvas at around 16k-32k pixels a side, and area too). Over that limit the canvas
      silently fails to paint and toDataURL hands back a stub, which downloadjs writes out
      as a .png that is not a PNG. Nothing throws anywhere along that path.

   So the table is drawn straight onto a canvas here instead: no clone, no stylesheets, no
   fonts to fetch, and the size is known BEFORE anything is rendered, so a table too big to
   be an image is refused with a reason instead of saved as a broken file.

   (A chart is still photographed - it is already a picture, and a small one. See
   exportNodeToPng.) */

/* Chrome, Edge and Firefox all paint a canvas up to 16,384 pixels a side; this stays just
   inside that. A band that would exceed it is not drawn - the rows are divided into more
   bands instead. */
const CANVAS_MAX_PX = 16000;

const TABLE_PNG = {
  pad: 24,
  rowHeight: 26,
  headerHeight: 32,
  cellPad: 10,
  minCol: 60,
  maxCol: 280,
  font: '12px "Inter", system-ui, -apple-system, sans-serif',
  headFont: 'bold 12px "Inter", system-ui, -apple-system, sans-serif',
  // the name is the first thing anyone looks for on a file they have been sent
  titleFont: 'bold 22px "Inter", system-ui, -apple-system, sans-serif',
  metaFont: '12px "Inter", system-ui, -apple-system, sans-serif',
};

/* The heading's geometry, in one place: drawPngHeading paints to these numbers, and
   drawTablePage and rowsPerPage reserve space by them. Split up, a taller title quietly
   overlapped the first row. */
const PNG_HEADING = { titleMiddle: 14, firstLine: 40, lineHeight: 18 };
const headingHeight = (lineCount) =>
  lineCount ? PNG_HEADING.firstLine + lineCount * PNG_HEADING.lineHeight + 4 : 40;

/* Column widths for the picture: the author's where they set one, so the image has the
   same proportions as the table and as the PDF.

   Where a column has none, one pass over the data sizes it to its widest value, capped so
   one long description cannot push the rest off the picture. */
const measureColumns = (ctx, headers, body, widths) =>
  headers.map((header, i) => {
    const saved = widths?.[i];
    if (Number.isFinite(saved) && saved > 0) return Math.ceil(saved);

    ctx.font = TABLE_PNG.headFont;
    let width = ctx.measureText(String(header)).width;
    ctx.font = TABLE_PNG.font;
    body.forEach((row) => {
      const cell = ctx.measureText(String(row[i] ?? "")).width;
      if (cell > width) width = cell;
    });
    return Math.min(TABLE_PNG.maxCol, Math.max(TABLE_PNG.minCol, Math.ceil(width) + TABLE_PNG.cellPad * 2));
  });

const clipText = (ctx, text, maxWidth) => {
  const value = String(text ?? "");
  if (ctx.measureText(value).width <= maxWidth) return value;
  let clipped = value;
  while (clipped.length > 1 && ctx.measureText(`${clipped}…`).width > maxWidth) {
    clipped = clipped.slice(0, -1);
  }
  return `${clipped}…`;
};

/* The naming block every picture carries, table or chart: the widget's title, and under
   it the board it belongs to and the filters in force. An image that leaves the app with
   no name on it cannot be identified again once it is in someone's inbox. */
const drawPngHeading = (ctx, { title, lines, x, y }) => {
  ctx.textBaseline = "middle";
  ctx.fillStyle = "#0f2557";
  ctx.font = TABLE_PNG.titleFont;
  ctx.fillText(String(title), x, y + PNG_HEADING.titleMiddle);

  ctx.font = TABLE_PNG.metaFont;
  ctx.fillStyle = "#64748b";
  lines.forEach(([label, value], i) => {
    ctx.fillText(`${label ? `${label}: ` : ""}${pdfText(value)}`, x, y + PNG_HEADING.firstLine + i * PNG_HEADING.lineHeight);
  });
};

/* ONE image of ONE band of rows. Nearly every table is a single band - this is only
   called more than once when the rows genuinely cannot fit in one picture (see
   exportRowsToPng). It draws what it is handed, so a band out of four looks exactly like
   a table that fitted whole, apart from the "Rows 1-450 of 1,800" line. */
const drawTablePage = ({ headers, body, columns, title, headingLines, pageLabel }) => {
  const { pad, rowHeight, headerHeight, cellPad } = TABLE_PNG;

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  const lines = pageLabel ? [...headingLines, ["", pageLabel]] : headingLines;
  const titleBlock = headingHeight(lines.length);
  const footerBlock = 26;
  const width = pad * 2 + columns.reduce((sum, w) => sum + w, 0);
  const height = pad * 2 + titleBlock + headerHeight + body.length * rowHeight + footerBlock;
  if (width > CANVAS_MAX_PX || height > CANVAS_MAX_PX) return null;

  // 2x where it fits, so the text is not soft on a high-density screen
  const scale = width * 2 <= CANVAS_MAX_PX && height * 2 <= CANVAS_MAX_PX ? 2 : 1;
  canvas.width = width * scale;
  canvas.height = height * scale;
  ctx.scale(scale, scale);

  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, width, height);

  drawPngHeading(ctx, { title, lines, x: pad, y: pad });

  let y = pad + titleBlock;

  ctx.fillStyle = "#0f2557";
  ctx.fillRect(pad, y, width - pad * 2, headerHeight);
  ctx.fillStyle = "#ffffff";
  ctx.font = TABLE_PNG.headFont;
  let x = pad;
  headers.forEach((header, i) => {
    ctx.save();
    ctx.beginPath();
    ctx.rect(x, y, columns[i], headerHeight);
    ctx.clip();
    ctx.fillText(clipText(ctx, header, columns[i] - cellPad * 2), x + cellPad, y + headerHeight / 2);
    ctx.restore();
    x += columns[i];
  });

  y += headerHeight;
  ctx.font = TABLE_PNG.font;
  body.forEach((row, r) => {
    if (r % 2) {
      ctx.fillStyle = "#f8fafc";
      ctx.fillRect(pad, y, width - pad * 2, rowHeight);
    }
    ctx.fillStyle = "#1f2937";
    x = pad;
    row.forEach((cell, i) => {
      ctx.save();
      ctx.beginPath();
      ctx.rect(x, y, columns[i], rowHeight);
      ctx.clip();
      ctx.fillText(clipText(ctx, cell, columns[i] - cellPad * 2), x + cellPad, y + rowHeight / 2);
      ctx.restore();
      x += columns[i];
    });

    ctx.strokeStyle = "#e2e8f0";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(pad, y + rowHeight - 0.5);
    ctx.lineTo(width - pad, y + rowHeight - 0.5);
    ctx.stroke();
    y += rowHeight;
  });

  ctx.font = '10px "Inter", system-ui, -apple-system, sans-serif';
  ctx.fillStyle = "#94a3b8";
  ctx.fillText(`Generated ${new Date().toLocaleString()}`, pad, y + 14);

  return canvas;
};

/* How many rows one picture can hold, worked out from the geometry rather than guessed,
   so the number moves with the row height and the heading if either changes. */
const rowsPerPage = (headingCount) => {
  const { pad, rowHeight, headerHeight } = TABLE_PNG;
  // headingCount + 1: a split export adds the "Rows 601-1,200 of 1,800" line
  const chrome = pad * 2 + headingHeight(headingCount + 1) + headerHeight + 26;
  return Math.max(1, Math.floor((CANVAS_MAX_PX - chrome) / rowHeight));
};

export const exportRowsToPng = async ({ rows, fields, widgetName, boardName, filters }) => {
  const matrix = buildExportMatrix({ rows, fields });
  if (!matrix) return false;

  const { headers, body } = matrix;
  const { title, lines: headingLines } = buildHeadingLines({ boardName, widgetName, filters });

  /* A picture is the one format with a ceiling, and it is the browser's: a canvas may not
     be more than about 16k pixels a side, and beyond that it silently stops painting. So
     a table taller than one image is split across several rather than refused - the rows
     all arrive, in order, each image carrying the same headers and saying which rows it
     holds. Anything that fits, which is nearly everything, is still a single file.

     The cap is on the number of images, not the number of rows: past it someone is asking
     for a wall of pictures and wants Excel or CSV. Counted before anything is measured,
     because measuring is a pass over every cell of a table we are about to turn away. */
  const perPage = rowsPerPage(headingLines.length);
  const pages = Math.ceil(body.length / perPage);
  if (pages > PNG_MAX_IMAGES) return "too-many-rows";

  /* Columns are measured once, over ALL the rows, so every band lines up with the others
     - a column that is wide on image 1 is the same width on image 4. */
  const probe = document.createElement("canvas").getContext("2d");
  if (!probe) return false;
  const columns = measureColumns(probe, headers, body, matrix.widths);

  const download = await loadDownload();
  const base = `${widgetName || boardName || "report"}_${timeStamp()}`;

  for (let page = 0; page < pages; page += 1) {
    const from = page * perPage;
    const slice = body.slice(from, from + perPage);
    const canvas = drawTablePage({
      headers,
      body: slice,
      columns,
      title,
      headingLines,
      // only when there is more than one, so an ordinary export says nothing about pages
      pageLabel:
        pages > 1
          ? `Rows ${(from + 1).toLocaleString()}–${(from + slice.length).toLocaleString()} of ${body.length.toLocaleString()} (image ${page + 1} of ${pages})`
          : "",
    });

    // too wide to draw at all - splitting rows cannot help that, so say so plainly
    if (!canvas) return "too-many-rows";

    const dataUrl = canvas.toDataURL("image/png");
    if (!isUsablePng(dataUrl)) throw new Error("The image could not be rendered");
    download(dataUrl, pages > 1 ? `${base}_${page + 1}of${pages}.png` : `${base}.png`, "image/png");

    // a breath between saves: browsers throttle a burst of downloads from one gesture
    if (pages > 1) await new Promise((resolve) => setTimeout(resolve, 350));
  }

  return { files: pages };
};

/* A canvas that exceeded what the browser would paint returns a stub from toDataURL - a
   handful of characters, or a bare "data:," - and writing that out is exactly how a .png
   that will not open gets made. It is checked before it reaches the disk. */
const saveCanvas = async (canvas, fileName) => {
  const dataUrl = canvas.toDataURL("image/png");
  if (!isUsablePng(dataUrl)) throw new Error("The image could not be rendered");

  const download = await loadDownload();
  download(dataUrl, fileName, "image/png");
  return true;
};

const isUsablePng = (dataUrl) =>
  typeof dataUrl === "string" && dataUrl.startsWith("data:image/png") && dataUrl.length > 1000;

/* A chart is already a picture, and a small one, so it is photographed - there is no
   array of numbers that would reproduce the shape.

   Bounded by a timeout because dom-to-image inlines every web font the document links
   before it draws anything, and this app links one from fonts.googleapis.com: where that
   request hangs, so did the export, with a spinner and no end to it. A minute is far
   longer than a chart needs and short enough to be an answer. */
const CAPTURE_TIMEOUT_MS = 60000;

const captureNode = async (node) => {
  const domtoimage = await loadDomToImage();
  const dataUrl = await Promise.race([
    domtoimage.toPng(node, {
      bgcolor: "#ffffff",
      width: node.scrollWidth || node.offsetWidth,
      height: node.scrollHeight || node.offsetHeight,
    }),
    new Promise((_resolve, reject) =>
      setTimeout(() => reject(new Error("The image took too long to render")), CAPTURE_TIMEOUT_MS),
    ),
  ]);

  if (!isUsablePng(dataUrl)) throw new Error("The image could not be rendered");
  return dataUrl;
};

export const exportNodeToPng = async ({ node, widgetName, boardName, filters }) => {
  if (!node) return false;
  const [dataUrl, download] = await Promise.all([captureNode(node), loadDownload()]);

  /* The capture on its own is a shape with no name on it - which chart, off which board,
     filtered how. The same heading a table's picture gets is drawn above it, by painting
     the capture onto a taller canvas rather than by dressing the widget up beforehand:
     nothing on the page moves, and the chart the reader is looking at is the chart in the
     file. */
  const titled = await addPngHeading(dataUrl, buildHeadingLines({ boardName, widgetName, filters }));
  download(titled || dataUrl, `${widgetName || boardName || "chart"}_${timeStamp()}.png`, "image/png");
  return true;
};

const addPngHeading = async (dataUrl, { title, lines }) => {
  const image = await new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    // the heading is a courtesy - a chart that will not re-load is still saved bare
    img.onerror = () => resolve(null);
    img.src = dataUrl;
  });
  if (!image) return null;

  const { pad } = TABLE_PNG;
  const titleBlock = headingHeight(lines.length);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  const width = image.width + pad * 2;
  const height = image.height + titleBlock + pad * 2;
  if (width > CANVAS_MAX_PX || height > CANVAS_MAX_PX) return null;

  canvas.width = width;
  canvas.height = height;
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, width, height);
  drawPngHeading(ctx, { title, lines, x: pad, y: pad });
  ctx.drawImage(image, pad, pad + titleBlock);

  const out = canvas.toDataURL("image/png");
  return isUsablePng(out) ? out : null;
};

/* Full screen for a single widget. The board-level button fullscreens the whole board
   through react-easyfullscreen; a widget has no such wrapper, so it goes straight to the
   browser API on its own element.

   The re-measure afterwards is not optional: react-grid-layout and ApexCharts only size
   themselves on mount and on window resize, and the resize that entering fullscreen
   fires lands before the new size is readable - without this the widget renders
   fullscreen at its old pixel width. */
export const toggleElementFullScreen = (element) => {
  if (!element) return;

  const current = document.fullscreenElement || document.webkitFullscreenElement;
  if (current) {
    const exit = document.exitFullscreen || document.webkitExitFullscreen || document.msExitFullscreen;
    if (exit) Promise.resolve(exit.call(document)).catch((err) => console.error("Fullscreen error:", err));
    return;
  }

  const request = element.requestFullscreen || element.webkitRequestFullscreen || element.msRequestFullscreen;
  if (!request) {
    console.warn("Full screen is not supported in this browser");
    return;
  }
  Promise.resolve(request.call(element))
    .then(() => setTimeout(() => window.dispatchEvent(new Event("resize")), 100))
    .catch((err) => console.error("Fullscreen error:", err));
};
